import React, { useMemo } from "react";
import styles from "../ResourcesPlanner.module.scss";
import { resourcesInRows } from "../engine/rowModel";
import { DAY_WIDTHS, TILE_DAYS, dayToX, tileFirstDay, tileOf } from "../engine/timelineGeometry";
import { usePlannerEngine, useData, useRowModel, useView, useViewport } from "../plannerContext";

/**
 * Marks the stretches of time that are still being loaded: for each resource on screen, every tile in the window
 * that its allocations are not held for yet is covered by a soft moving highlight over all of the resource's rows.
 */
function CoverageShimmer() {
  const { timeline } = usePlannerEngine();
  const model = useRowModel();
  const allocs = useData((state) => state.data.allocs);
  const viewType = useView((state) => state.viewType);
  const firstDay = useViewport((state) => state.firstDay);
  const lastDay = useViewport((state) => state.lastDay);
  const firstRow = useViewport((state) => state.firstRow);
  const lastRow = useViewport((state) => state.lastRow);

  const dayWidth = DAY_WIDTHS[viewType];

  const rects = useMemo(() => {
    const result = [];
    const firstTile = Math.max(0, tileOf(timeline, firstDay));
    const lastTile = Math.min(timeline.tileCount - 1, tileOf(timeline, lastDay));

    for (const resourceId of resourcesInRows(model, firstRow, lastRow)) {
      const covered = allocs[resourceId]?.tiles;
      const span = model.spans[resourceId];
      if (!covered || !span) continue;

      const top = model.tops[span.first];
      const lastRowOfResource = model.rows[span.first + span.count - 1];
      const height = model.tops[span.first + span.count - 1] + lastRowOfResource.height - top;

      for (let tile = firstTile; tile <= lastTile; tile += 1) {
        if (covered.has(tile)) continue;
        result.push({ key: `${resourceId}:${tile}`, left: dayToX(timeline, tileFirstDay(timeline, tile), dayWidth), width: TILE_DAYS * dayWidth, top, height });
      }
    }
    return result;
  }, [timeline, model, allocs, firstDay, lastDay, firstRow, lastRow, dayWidth]);

  return (
    <>
      {rects.map((rect) => (
        <div key={rect.key} className={styles.shimmer} style={{ left: rect.left, width: rect.width, top: rect.top, height: rect.height }} />
      ))}
    </>
  );
}

export default React.memo(CoverageShimmer);
